import { Injectable } from '@angular/core';
import { WritableSignal, signal } from '@angular/core';
import {
  INotification,
  NotificationSystemService,
} from 'src/app/common-components/services/common-notification-system.service';
import {
  Attendant,
  AttendantRequest,
  BaseAttendant,
  ProviderDirectoryFunctionService,
} from 'src/app/services/providerdirectoryapi/providerdirectoryfunctionapi.services';

import { AdminApprovalService } from '../admin/admin-approval.service';

@Injectable({
  providedIn: 'root',
})
export class AttendantProfileService {
  private _entity: WritableSignal<Attendant | undefined> = signal(undefined);
  private _parameters: WritableSignal<string> = signal<string>('');
  private _success: WritableSignal<boolean> = signal<boolean>(false);
  private _load: WritableSignal<boolean> = signal<boolean>(false);

  private _token = '7b49e2d5-5bb4-419d-a80e-af5bda36ed6b';
  isAdmin = false;

  constructor(
    private notificationSystemService: NotificationSystemService,
    private providerDirectoryFunctionService: ProviderDirectoryFunctionService,
    private adminApprovalService: AdminApprovalService
  ) {}

  private localSave() {
    sessionStorage.setItem(this._token, this._parameters());
  }

  private localLoad() {
    const check = sessionStorage.getItem(this._token);
    if (check !== null) {
      this._parameters.set(sessionStorage.getItem(this._token) ?? '');
      return true;
    }
    return false;
  }

  async search(data?: string) {
    if (data !== undefined) {
      this._parameters.set(data);
    } else if (this._parameters()) {
      data = this._parameters();
    } else {
      if (!this.localLoad()) {
        return;
      }
      data = this._parameters();
    }
    this._load.set(true);
    this.providerDirectoryFunctionService.getAttendantById(data).subscribe({
      next: res => {
        if (res && res.data && typeof res.data.userId === 'string' && res.data.userId.length > 0) {
          const attendant: Attendant = new Attendant();
          Object.assign(attendant, res.data);
          this._entity.set(attendant);
          this.adminApprovalService.getReviewRequest(attendant.userId);
        }
      },
      error: error => {
        if (error.message) {
          const notification: INotification = { type: 'error', content: error.message };
          this.notificationSystemService.setNotification(notification);
        }
      },
      complete: () => {
        this._load.set(false);
      },
    });
  }

  async searchPublic(data?: string) {
    if (data !== undefined) {
      this._parameters.set(data);
    } else if (this._parameters()) {
      data = this._parameters();
    } else {
      if (!this.localLoad()) {
        return;
      }
      data = this._parameters();
    }
    this._load.set(true);
    this.providerDirectoryFunctionService.getPublicAttendantProfileById(data).subscribe({
      next: res => {
        if (res && res.data && typeof res.data.userId === 'string' && res.data.userId.length > 0) {
          const attendant: Attendant = new Attendant();
          Object.assign(attendant, res.data);
          this._entity.set(attendant);
          if (this.isAdmin) {
            this.adminApprovalService.getReviewRequest(attendant.userId);
          }
        }
      },
      error: error => {
        if (error.message) {
          const notification: INotification = { type: 'error', content: error.message };
          this.notificationSystemService.setNotification(notification);
        }
      },
      complete: () => {
        this._load.set(false);
      },
    });
  }

  public update(data?: Attendant, full = true): void {
    const attendant: AttendantRequest = new AttendantRequest(data);
    attendant.fullUpdate = full;
    this._load.set(true);
    this.providerDirectoryFunctionService.updateAttendant(data ? attendant : this._entity()).subscribe({
      next: res => {
        if (res) {
          if (!res.isSuccess) {
            const notification: INotification = { type: 'error', content: res.message ?? '' };
            this.notificationSystemService.setNotification(notification);
          } else {
            this._entity.set(data);
          }
        }
      },
      error: error => {
        if (error.message) {
          const notification: INotification = { type: 'error', content: error.message };
          this.notificationSystemService.setNotification(notification);
        }
      },
      complete: () => {
        this._load.set(false);
      },
    });
  }

  public updateAttendantField<K extends keyof Attendant>(key: K, value: Attendant[K]): void {
    if (this._entity) {
      const updated = new Attendant({ ...this._entity(), [key]: value });
      this._entity.set(updated);
    }
  }

  public affirm(data: string): void {
    this._load.set(true);
    this.providerDirectoryFunctionService.affirm(data).subscribe({
      next: res => {
        if (res) {
          this._success.set(res.isSuccess ?? false);
          if (!res.isSuccess) {
            const notification: INotification = { type: 'error', content: res.message ?? '' };
            this.notificationSystemService.setNotification(notification);
          } else {
            const notification: INotification = { type: 'success', content: 'Reaffirmation Successful' };
            this.notificationSystemService.setNotification(notification);
          }
        }
      },
      error: error => {
        if (error.message) {
          const notification: INotification = { type: 'error', content: error.message ?? '' };
          this.notificationSystemService.setNotification(notification);
        }
      },
      complete: () => {
        this._load.set(false);
      },
    });
  }

  public fetchData(override = false): WritableSignal<Attendant | undefined> {
    if (override) {
      this.search();
    }
    return this._entity;
  }

  public fetchPublicData(override = false): WritableSignal<BaseAttendant> {
    if (override) {
      this.searchPublic();
    }
    return this._entity as WritableSignal<BaseAttendant>;
  }

  public getSearchParameters(): WritableSignal<string> {
    return this._parameters;
  }

  public setSearchParameters(data: string) {
    this._parameters.set(data);
    this.localSave();
  }

  public getLoad(): WritableSignal<boolean> {
    return this._load;
  }

  public getSuccess(): WritableSignal<boolean> {
    return this._success;
  }

  public clear(): void {
    this._entity.set(undefined);
    this._parameters.set('');
  }

  public clearEntity(): void {
    this._entity.set(undefined);
  }

  public clearParamters(): void {
    this._parameters.set('');
  }

  public clearLoad(): void {
    this._load.set(false);
  }

  public setIsAdmin(isAdmin: boolean) {
    this.isAdmin = isAdmin;
  }
}
